import React from 'react';
import i18n from 'i18next';

import { Avatar, Tooltip, AvatarGroup } from '@mui/material';
import {
  GridColDef,
  GridValidRowModel,
  getGridDateOperators,
  getGridStringOperators,
} from '@mui/x-data-grid-pro';

import { DocumentStatuses } from '../enums/documentStatuses';
import { DocumentColumnType } from '../enums/documentColumnType';
import GetStatusName from '../../../utils/folders/getStatusName';
import RenderNameField from '../../../utils/folders/renderNameField';
import RenderStatusField from '../../../utils/folders/renderStatusField';
import RenderDateTimeField from '../../../utils/folders/renderDateTimeField';
import RenderSignerDetailsField from '../../../utils/folders/renderSignerDetailsField';
import RenderDocumentActionsField from '../../../utils/folders/renderDocumentActionsField';

// ----------------------------------------------------------------------

export const DEFAULT_COLUMNS: GridColDef<GridValidRowModel>[] = [
  {
    field: 'name',
    headerName: i18n.t('documents.analytics.columns.name'),
    headerClassName: 'documents-grid--header',
    type: DocumentColumnType.String,
    hideable: false,
    disableReorder: true,
    minWidth: 250,
    renderCell: (params) => {
      const { extension, name, hasUnseenAccompanyingDocuments } = params.row;

      return (
        <RenderNameField
          name={name}
          extension={extension}
          hasUnseenAccompanyingDocuments={hasUnseenAccompanyingDocuments}
        />
      );
    },
    filterOperators: getGridStringOperators().filter(
      (operator) => operator.value !== 'isEmpty' && operator.value !== 'isNotEmpty'
    ),
  },
  {
    field: 'created',
    headerName: i18n.t('documents.analytics.columns.created'),
    headerClassName: 'documents-grid--header',
    type: DocumentColumnType.Date,
    minWidth: 160,
    valueGetter: (value: string) => new Date(value),
    renderCell: (params) => {
      const { created } = params.row;

      return <RenderDateTimeField date={created} format="dateTime" />;
    },
    filterOperators: getGridDateOperators().filter(
      (operator) => operator.value !== 'isEmpty' && operator.value !== 'isNotEmpty'
    ),
  },
  {
    field: 'modified',
    headerName: i18n.t('documents.analytics.columns.modified'),
    headerClassName: 'documents-grid--header',
    type: DocumentColumnType.Date,
    width: 160,
    valueGetter: (value: string) => new Date(value),
    renderCell: (params) => {
      const { modified } = params.row;

      return <RenderDateTimeField date={modified} format="dateTime" />;
    },
    filterOperators: getGridDateOperators().filter(
      (operator) => operator.value !== 'isEmpty' && operator.value !== 'isNotEmpty'
    ),
  },
  {
    field: 'ownerFullName',
    headerName: i18n.t('documents.analytics.columns.owner'),
    headerClassName: 'documents-grid--header',
    type: DocumentColumnType.String,
    minWidth: 160,
    renderCell: (params) => {
      const { ownerFullName, ownerEmail } = params.row;

      return ownerFullName || ownerEmail;
    },
    filterOperators: getGridStringOperators().filter(
      (operator) => operator.value !== 'isEmpty' && operator.value !== 'isNotEmpty'
    ),
    sortable: false,
  },
  {
    field: 'status',
    headerName: i18n.t('documents.analytics.columns.status'),
    headerClassName: 'documents-grid--header',
    type: DocumentColumnType.SingleSelect,
    minWidth: 200,
    valueOptions: [
      {
        value: DocumentStatuses.New + 1,
        label: <GetStatusName status={DocumentStatuses.New} />,
      },
      {
        value: DocumentStatuses.Completed + 1,
        label: <GetStatusName status={DocumentStatuses.Completed} />,
      },
      {
        value: DocumentStatuses.WaitingForOthers + 1,
        label: <GetStatusName status={DocumentStatuses.WaitingForOthers} />,
      },
      {
        value: DocumentStatuses.WaitingForMe + 1,
        label: <GetStatusName status={DocumentStatuses.WaitingForMe} />,
      },
      {
        value: DocumentStatuses.Rejected + 1,
        label: <GetStatusName status={DocumentStatuses.Rejected} />,
      },
      {
        value: DocumentStatuses.Voided + 1,
        label: <GetStatusName status={DocumentStatuses.Voided} />,
      },
      {
        value: DocumentStatuses.WaitingForOthersApprove + 1,
        label: <GetStatusName status={DocumentStatuses.WaitingForOthersApprove} />,
      },
      {
        value: DocumentStatuses.WaitingForMeApprove + 1,
        label: <GetStatusName status={DocumentStatuses.WaitingForMeApprove} />,
      },
      {
        value: DocumentStatuses.WaitingForRelatedDocuments + 1,
        label: <GetStatusName status={DocumentStatuses.WaitingForRelatedDocuments} />,
      },
      {
        value: DocumentStatuses.WaitingForMeToAcknowledge + 1,
        label: <GetStatusName status={DocumentStatuses.WaitingForMeToAcknowledge} />,
      },
      {
        value: DocumentStatuses.WaitingForOthersToAcknowledge + 1,
        label: <GetStatusName status={DocumentStatuses.WaitingForOthersToAcknowledge} />,
      },
      // {
      //   value: DocumentStatuses.InProgress + 1,
      //   label: <GetStatusName status={DocumentStatuses.InProgress} />,
      // },
      {
        value: DocumentStatuses.WaitingForMeToFillForm + 1,
        label: <GetStatusName status={DocumentStatuses.WaitingForMeToFillForm} />,
      },
      {
        value: DocumentStatuses.WaitingForOthersToFillForm + 1,
        label: <GetStatusName status={DocumentStatuses.WaitingForOthersToFillForm} />,
      },
      {
        value: DocumentStatuses.FormFilled + 1,
        label: <GetStatusName status={DocumentStatuses.FormFilled} />,
      },
    ],
    renderCell: (params) => {
      const { status } = params.row;

      return <RenderStatusField status={status} />;
    },
  },
  {
    field: 'signatures',
    headerName: i18n.t('documents.analytics.columns.signatures'),
    headerClassName: 'documents-grid--header',
    type: DocumentColumnType.String,
    renderCell: (params) => {
      const { alreadySigned, totalSigners, signerDetails, hideSensitiveInformationForUser } =
        params.row;

      return (
        <RenderSignerDetailsField
          signatures={`${alreadySigned}/${totalSigners}`}
          signerDetails={signerDetails}
          hideSensitiveInformation={hideSensitiveInformationForUser}
        />
      );
    },
    sortable: false,
    filterOperators: getGridStringOperators().filter(
      (operator) => operator.value !== 'isEmpty' && operator.value !== 'isNotEmpty'
    ),
  },
  {
    field: 'tags',
    headerName: i18n.t('documents.analytics.columns.tags'),
    headerClassName: 'documents-grid--header',
    type: DocumentColumnType.SingleSelect,
    renderCell: (params) => {
      const { tags } = params.row;

      return (
        <AvatarGroup
          spacing="medium"
          max={3}
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {tags.length > 0 &&
            tags.map((tag) => (
              <Tooltip
                title={`${tag.name}`}
                arrow
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -2],
                        },
                      },
                    ],
                  },
                }}
              >
                <Avatar
                  sx={{ width: 24, height: 24, backgroundColor: tag.color, color: '#fff' }}
                  alt={`${tag.name}`}
                >
                  {`${tag.name}`[0].toUpperCase()}
                </Avatar>
              </Tooltip>
            ))}
        </AvatarGroup>
      );
    },
    sortable: false,
    valueOptions: [],
  },
  {
    field: 'actions',
    headerName: i18n.t('documents.analytics.columns.actions'),
    headerClassName: 'documents-grid--header',
    align: 'center',
    type: DocumentColumnType.Number,
    width: 50,
    renderCell: (params) => <RenderDocumentActionsField row={params.row} />,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
  },
];

export const COMMENT_COLUMN: GridColDef<GridValidRowModel>[] = [
  {
    field: 'comment',
    headerName: i18n.t('documents.analytics.columns.comment'),
    headerClassName: 'documents-grid--header',
    type: DocumentColumnType.String,
  },
];

export const EXPIRATION_DATE_COLUMN: GridColDef<GridValidRowModel>[] = [
  {
    field: 'deadline',
    headerName: i18n.t('documents.analytics.columns.expirationDate'),
    headerClassName: 'documents-grid--header',
    type: DocumentColumnType.Date,
    minWidth: 160,
    valueGetter: (value: string) => new Date(value),
    renderCell: (params) => {
      const { deadline } = params.row;

      if (deadline) {
        return <RenderDateTimeField date={deadline} format="date" />;
      }

      return deadline;
    },
    filterOperators: getGridDateOperators().filter(
      (operator) => operator.value !== 'isEmpty' && operator.value !== 'isNotEmpty'
    ),
  },
];
